import { createStore, createEvent, sample, createEffect } from "effector";
import { ModelContract, ModuleDescription } from "../../../shared/module-description";
import { IUserAuthContract } from "../model";
import { IRouterContract } from "@features/shared/router";
import { validatePasswords } from "@features/helpers";
import {
    confirmEmailChanged,
    confirmEmailScreenCompleted,
    confirmEmailScreenPresented,
    setPasswordScreenCompleted,
    setPasswordScreenPresented
} from "@features/shared/events";
import { ISignInFormContract } from "./sign-in.form";

const inputEmailChanged = createEvent<string>();
const purchaseEmailChanged = createEvent<string>();
const confirmButtonClicked = createEvent();
const emailScreenPresented = createEvent();

const passwordChanged = createEvent<string>();
const passwordConfirmationChanged = createEvent<string>();
const savePasswordButtonClicked = createEvent();
const passwordScreenPresented = createEvent();

const $purchaseEmail = createStore<string>("").on(purchaseEmailChanged, (s, p) => p);
const $inputEmail = createStore<string>("")
    .on(inputEmailChanged, (s, p) => p)
    .on($purchaseEmail, (s, p) => p);
const $isConfirmEmailLoading = createStore<boolean>(false);

const $password = createStore<string>("").on(passwordChanged, (s, p) => p);
const $passwordConfirmation = createStore<string>("").on(passwordConfirmationChanged, (s, p) => p);
const $isSetPasswordLoading = createStore<boolean>(false);

const $errorMessage = createStore<string>("").reset(
    confirmButtonClicked,
    savePasswordButtonClicked,
    passwordConfirmationChanged,
    inputEmailChanged
);

const trackConfirmEmailChangedFx = createEffect(
    ({ purchaseEmail, confirmEmail }: { purchaseEmail: string; confirmEmail: string }) => {
        confirmEmailChanged({ purchaseEmail, confirmEmail });
    }
);

const trackEmailConfirmationScreenPresentedFx = createEffect(() => {
    confirmEmailScreenPresented();
});

const trackEmailConfirmationScreenCompletedFx = createEffect(() => {
    confirmEmailScreenCompleted();
});

const trackSetPasswordScreenPresentedFx = createEffect(() => {
    setPasswordScreenPresented();
});

const trackSetPasswordScreenCompletedFx = createEffect(() => {
    setPasswordScreenCompleted();
});


sample({
    source: { $password, $passwordConfirmation },
    fn: (s) => validatePasswords(s.$password, s.$passwordConfirmation),
    target: $errorMessage
});

sample({
    clock: emailScreenPresented,
    target: trackEmailConfirmationScreenPresentedFx
});

sample({
    clock: passwordScreenPresented,
    target: trackSetPasswordScreenPresentedFx
});

const contract = new ModelContract(
    {
        $inputEmail,
        $purchaseEmail,
        $password,
        $passwordConfirmation,
        $isConfirmEmailLoading,
        $isSetPasswordLoading,
        $errorMessage
    },
    {
        inputEmailChanged,
        purchaseEmailChanged,
        confirmButtonClicked,
        passwordChanged,
        passwordConfirmationChanged,
        savePasswordButtonClicked,
        emailScreenPresented,
        passwordScreenPresented
    },
    {},
    {}
);

type IRegistrationContract = typeof contract;

export class RegistrationModule extends ModuleDescription<IRegistrationContract> {
    constructor(
        public userAuthModule: IUserAuthContract,
        public routerModule: IRouterContract,
        public signInFormModule: ISignInFormContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: confirmButtonClicked,
            source: { $inputEmail, $purchaseEmail },
            fn: (s) => ({
                emailFromPurchase: s.$purchaseEmail.toLowerCase().trim(),
                newEmail: s.$inputEmail.toLowerCase().trim()
            }),
            target: this.userAuthModule.effects.confirmEmailAfterPurchaseFx
        });

        sample({
            clock: this.userAuthModule.effects.confirmEmailAfterPurchaseFx.pending,
            target: $isConfirmEmailLoading
        });

        sample({
            clock: this.userAuthModule.effects.confirmEmailAfterPurchaseFx.doneData,
            fn: () => "/auth/create-password",
            target: [this.routerModule.events.routerPush, trackEmailConfirmationScreenCompletedFx]
        });

        sample({
            clock: this.userAuthModule.effects.confirmEmailAfterPurchaseFx.doneData,
            source: { $purchaseEmail, $inputEmail },
            filter: (s) => s.$purchaseEmail.toLowerCase().trim() !== s.$inputEmail.toLowerCase().trim(),
            fn: (s) => ({
                purchaseEmail: s.$purchaseEmail.toLowerCase(),
                confirmEmail: s.$inputEmail.toLowerCase()
            }),
            target: trackConfirmEmailChangedFx
        });

        sample({
            clock: savePasswordButtonClicked,
            source: { $inputEmail, $password },
            fn: (s) => ({
                email: s.$inputEmail.toLowerCase().trim(),
                password: s.$password
            }),
            target: this.userAuthModule.effects.setPasswordWithoutCodeFx
        });

        sample({
            clock: this.userAuthModule.effects.setPasswordWithoutCodeFx.pending,
            target: $isSetPasswordLoading
        });

        sample({
            clock: this.userAuthModule.effects.setPasswordWithoutCodeFx.doneData,
            source: { $inputEmail, $password },
            filter: (s) => s.$inputEmail.length > 0 && s.$password.length > 0,
            fn: (s) => ({
                email: s.$inputEmail,
                password: s.$password
            }),
            target: [
                this.userAuthModule.events.signInRequested,
                trackSetPasswordScreenCompletedFx
            ]
        });

        sample({
            clock: [
                this.userAuthModule.effects.confirmEmailAfterPurchaseFx.failData,
                this.userAuthModule.effects.setPasswordWithoutCodeFx.failData
            ],
            fn: (e: unknown) => (e as any)?.error ?? "Unexpected error",
            target: $errorMessage
        });

        sample({
            clock: this.userAuthModule.effects.confirmEmailAfterPurchaseFx.failData,
            filter: (e: unknown) => {
                const err = e as any;
                const status = err?.status ?? err?.error?.status;
                return status === 409 || err?.code === "user_exists";
            },
            fn: () => "/auth/signin",
            target: this.routerModule.events.routerPush
        });

        sample({
            clock: this.userAuthModule.effects.confirmEmailAfterPurchaseFx.failData,
            filter: (e: unknown) => {
                const err = e as any;
                const status = err?.status ?? err?.error?.status;
                return status === 409 || err?.code === "user_exists";
            },
            fn: (e: unknown) => {
                return (e as any)?.email ?? "";
            },
            target: this.signInFormModule.events.emailChanged
        });
    }
}
